import {
  IProcessFlowVariableMap,
  IProcessFlowVariableMapOptions,
} from "~/interfaces/processFlowInterfaces"
import { RelationType } from '~/models/relationType'
import { ITrackedModel } from '~/interfaces/modelInterfaces'
import IRepo from '~/interfaces/repo'
import Repo from '~/state/modules/repo'
import { IClientIdable } from '~/interfaces/propertyInterfaces'
import ProcessFlowBuilderModel from "~/models/tracked/processFlowBuilderModel";
import ProcessFlowMappedVariable from "~/models/processFlows/steps/processFlowMappedVariable";
import {ProcessFlowOperator} from "~/models/processFlows/enums/processFlowOperator";
import {Hash} from "~/interfaces/hashInterfaces";
import {ProcessFlowSetDataStep} from "~/models/processFlows/steps/processFlowSetDataStep";
import {ProcessFlowSaveDataStep} from "~/models/processFlows/steps/processFlowSaveDataStep";
import {ModelType} from '~/enums/modelType'
import TrackedArrayProxy from '../../../../formability/form_builder/models/trackedArrayProxy'

type MappableParent = ProcessFlowSetDataStep | ProcessFlowSaveDataStep
export class SetDataVariableMap extends ProcessFlowBuilderModel implements IProcessFlowVariableMap {
  parent: MappableParent

  value_reference: ProcessFlowMappedVariable
  variable_reference: ProcessFlowMappedVariable
  process_operator: ProcessFlowOperator
  process_operator_params: any[]

  sort_order: number

  get TRACKED_ARRAYS(): string[] {
    return ["process_operator_params"];
  }

  constructor(parent: MappableParent, options: IProcessFlowVariableMapOptions = {firstCreation: false} ) {
    super();
    this.parent = parent;
    this._nameInParent = 'variable_maps'

    this.defineClientId();
    this.defineProperty('process_operator', null);
    this.defineProperty('process_operator_params', TrackedArrayProxy.create(Object.assign([], {
      parent: this,
      key: 'process_operator_params',
    })))

    if (options.firstCreation) {
      // do not setup defaults when loading json from backend
      // will result in hard-to track down overrides in change tracking
      this.initDefaults();
    }
  }

  initDefaults() {
    const valueReference = new ProcessFlowMappedVariable('value_reference');
    const variableReference = new ProcessFlowMappedVariable('variable_reference');
    valueReference.attachToParent(this);
    variableReference.attachToParent(this);
  }

  // ******************** RELATIONS AND REPOS ******************** //
  static repo: IRepo<SetDataVariableMap> = new Repo<SetDataVariableMap>('setDataVariableMaps', SetDataVariableMap, {});
  repo: IRepo<this>;

  addToRepo(): void {
    SetDataVariableMap.repo.add(this)
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      "value_reference": { type: ProcessFlowMappedVariable, relationType: RelationType.singleObject },
      "variable_reference": { type: ProcessFlowMappedVariable, relationType: RelationType.singleObject },
    }
  }

  createEmbeddedObject = {
    value_reference: (parent, json: Hash & Partial<IClientIdable>): ProcessFlowMappedVariable => {
      return new ProcessFlowMappedVariable('value_reference').loadFromJson(json, parent)
    },
    variable_reference: (parent, json: Hash & Partial<IClientIdable>): ProcessFlowMappedVariable => {
      return new ProcessFlowMappedVariable('variable_reference').loadFromJson(json, parent)
    },
  }


  initializeEmbedded(parent: SetDataVariableMap & ITrackedModel, item: ProcessFlowMappedVariable, position: number = null, json: Hash = {}, selectItem: boolean = true) {
    item.triggerChanges = true
    item.parent = parent
    item.startTracking()
    this.placeChildrenInRelations(item, item.rootName, this)
    parent.trackCreationDeletion(item.client_id, null, item.asJson(), item)
    return item
  }

  get collectionKey() {
    return this._nameInParent
  }

  get rootName(): string {
    return this._nameInParent;
  }

  get isCollectionRoot(): boolean {
    return true;
  }

  // ******************** SORTABLE******************** //

  get isLast(): boolean {
    return this.sort_order === this.parent.nextSortOrderFor(this.rootName) - 1;
  }

  get isFirst(): boolean {
    return this.sort_order <= 0;
  }

  moveUp(): void {
    if (!this.isFirst) {
      this.sort_order--;
      this.parent.variableMapsHandler.updateElementPositions(this.parent, this, this.sort_order);
    }
  }

  moveDown(): void {
    if (!this.isLast) {
      this.sort_order++;
      this.parent.variableMapsHandler.updateElementPositions(this.parent, this, this.sort_order);
    }
  }
  // ******************** SORTABLE END******************** //

  get canBeRemoved(): boolean {
    return this.parent.variableMapsArray.length > 1;
  }

  /**
   * Use this when the destination (variable_map) changes to ensure the source will always be a compatible value
   */
  resetSource(): void {
    if (this.variable_reference.source_variable_path.lastNodeService.referenceIsPickList()) {
      this.value_reference.source_constant_type = ModelType.Array
      this.value_reference.source_constant = []
    } else {
      this.value_reference.source_constant_type = ModelType.Text
      this.value_reference.source_constant = null
    }
    this.value_reference.source_type = this.value_reference.valueReferenceVariableTypes[0]
    this.value_reference.source_formula = null
    if (this.value_reference.source_variable_path) {
      this.value_reference.source_variable_path.nodes.forEach(n => n.destroy())
      this.value_reference.source_variable_path.nodes = []
    }
  }
}